import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { JobService } from '../../../core/services/job.service';
import { Job, JobStatus } from '../../../core/models/job.model';

type ViewMode = 'day' | 'week' | 'month';

const VISIBLE_STATUSES: JobStatus[] = ['Scheduled', 'EnRoute', 'Arrived', 'InProgress', 'Completed', 'Cancelled'];

@Component({
  selector: 'app-schedule-view',
  imports: [CommonModule],
  templateUrl: './schedule-view.html',
  styleUrl: './schedule-view.scss',
})
export class ScheduleView {
  private readonly jobService = inject(JobService);

  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly jobs = signal<Job[]>([]);

  protected readonly weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  protected readonly viewMode = signal<ViewMode>('week');
  protected readonly anchorDate = signal<Date>(this.dateOnly(new Date()));

  protected readonly weekDates = computed(() => {
    const start = this.startOfWeek(this.anchorDate());
    return Array.from({ length: 7 }, (_, i) => this.addDays(start, i));
  });

  protected readonly monthDates = computed(() => {
    const start = this.startOfWeek(this.startOfMonth(this.anchorDate()));
    const end = this.startOfWeek(this.endOfMonth(this.anchorDate()));
    const days = this.daysBetween(start, end) + 7;
    return Array.from({ length: days }, (_, i) => this.addDays(start, i));
  });

  protected readonly visibleDates = computed(() => {
    switch (this.viewMode()) {
      case 'week': return this.weekDates();
      case 'month': return this.monthDates();
      default: return [this.anchorDate()];
    }
  });

  protected readonly rangeLabel = computed(() => {
    if (this.viewMode() === 'day') {
      return this.anchorDate().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
    if (this.viewMode() === 'month') {
      return this.anchorDate().toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
    const dates = this.weekDates();
    const start = dates[0];
    const end = dates[6];
    const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const endLabel = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startLabel} - ${endLabel}`;
  });

  protected readonly jobsByDate = computed(() => {
    const map = new Map<string, Job[]>();
    for (const job of this.jobs()) {
      if (!job.scheduledDate || !VISIBLE_STATUSES.includes(job.status)) continue;
      const key = job.scheduledDate.slice(0, 10);
      const list = map.get(key) ?? [];
      list.push(job);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.scheduledDate ?? '').localeCompare(b.scheduledDate ?? ''));
    }
    return map;
  });

  constructor() {
    this.loadJobs();
  }

  private loadJobs(): void {
    this.loading.set(true);
    this.error.set(null);

    const dates = this.visibleDates();
    const rangeStart = dates[0];
    const rangeEnd = this.addDays(dates[dates.length - 1], 1);

    this.jobService.getJobs({
      scheduledFrom: `${this.dateKey(rangeStart)}T00:00:00`,
      scheduledTo: `${this.dateKey(rangeEnd)}T00:00:00`,
      pageSize: 500,
    }).subscribe({
      next: (result) => {
        this.jobs.set(result.items);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load the schedule. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  protected setViewMode(mode: ViewMode): void {
    this.viewMode.set(mode);
    this.loadJobs();
  }

  protected goToPrevious(): void {
    if (this.viewMode() === 'month') {
      this.anchorDate.set(this.addMonths(this.anchorDate(), -1));
    } else {
      this.anchorDate.set(this.addDays(this.anchorDate(), this.viewMode() === 'week' ? -7 : -1));
    }
    this.loadJobs();
  }

  protected goToNext(): void {
    if (this.viewMode() === 'month') {
      this.anchorDate.set(this.addMonths(this.anchorDate(), 1));
    } else {
      this.anchorDate.set(this.addDays(this.anchorDate(), this.viewMode() === 'week' ? 7 : 1));
    }
    this.loadJobs();
  }

  protected goToToday(): void {
    this.anchorDate.set(this.dateOnly(new Date()));
    this.loadJobs();
  }

  protected jobsFor(date: Date): Job[] {
    return this.jobsByDate().get(this.dateKey(date)) ?? [];
  }

  protected isToday(date: Date): boolean {
    return this.dateKey(date) === this.dateKey(this.dateOnly(new Date()));
  }

  protected isCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.anchorDate().getMonth() && date.getFullYear() === this.anchorDate().getFullYear();
  }

  protected formatDayName(date: Date): string {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }

  protected formatDayNumber(date: Date): number {
    return date.getDate();
  }

  protected formatTime(iso: string | null): string {
    if (!iso || iso.length < 16) return '';
    const [h, m] = iso.slice(11, 16).split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
  }

  protected statusColor(status: JobStatus): string {
    switch (status) {
      case 'Created': return '#64748B';
      case 'Assigned': return '#00B9F2';
      case 'Scheduled': return '#14b8a6';
      case 'EnRoute': return '#F29727';
      case 'Arrived': return '#a855f7';
      case 'InProgress': return '#eab308';
      case 'Completed': return '#8DC63F';
      case 'Cancelled': return '#ef4444';
      default: return '#64748B';
    }
  }

  private dateOnly(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private startOfWeek(date: Date): Date {
    return this.addDays(date, -date.getDay());
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private endOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }

  private addMonths(date: Date, months: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
  }

  private daysBetween(start: Date, end: Date): number {
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  }

  private dateKey(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
